/**
 * The catalog of business event names an EVENT-triggered notification
 * template can bind to. Sourced directly from every distinct audit
 * `eventType` literal emitted across the backend (`new AuditEventDraft(...)`
 * call sites) — there's no dedicated event-type enum on the backend today,
 * so this list is the closest thing to a canonical catalog and needs
 * updating here if a new audit event type is introduced there.
 *
 * Grouped by domain for the dropdown. A handful of purely internal/system
 * events (the SMS and EMAIL outbox lifecycle, SECURITY auth/session events)
 * are included for completeness since the ask was "all the events" — most
 * tenant-admin use cases will pick from PTP/Trip/Case/Cash/Recovery.
 */
/**
 * The events that can actually deliver a notification.
 *
 * Everything else in the catalog below is emitted into the audit log but has
 * no notification call site and no way to resolve a recipient, so a template
 * bound to it can never send. The backend rejects those on save; the dropdown
 * offers only these so an admin never configures something inert. Keep in step
 * with `NotificationEvent` in platform-core.
 */
export const DELIVERABLE_EVENTS: readonly string[] = [
  'PTP.OPENED',
  'PTP.BROKEN',
  'CASH.RECEIPT_CAPTURED',
  'CASE.CURED',
]

export const NOTIFICATION_EVENT_GROUPS: { group: string; events: string[] }[] = [
  {
    group: 'PTP',
    events: [
      'PTP.OPENED',
      'PTP.BROKEN',
      'PTP.KEPT',
      'PTP.PARTIAL',
      'PTP.CANCELLED',
      'PTP.RESOLVED_RETROACTIVELY',
    ],
  },
  {
    group: 'Case',
    events: [
      'CASE.OPENED',
      'CASE.UPDATED',
      'CASE.CURED',
      'CASE.DISPOSITION_RECORDED',
      'CASE.PRIORITY_OVERRIDDEN',
    ],
  },
  {
    group: 'Cash',
    events: [
      'CASH.LOT_OPENED',
      'CASH.LOT_DEPOSITED',
      'CASH.LOT_CONFIRMED',
      'CASH.LOT_DISCREPANCY',
      'CASH.LOT_REJECTED',
      'CASH.RECEIPT_CAPTURED',
      'CASH.CEILING_OVERRIDDEN',
      'CASH.RECEIPT_REVIEW_CLEARED',
    ],
  },
  {
    group: 'Recovery',
    events: ['RECOVERY.ACCRUAL_DEFERRED', 'RECOVERY.CONFIRMED'],
  },
  {
    group: 'Reconciliation',
    events: ['RECON.FINDING_RAISED', 'RECON.FINDING_AUTO_RESOLVED', 'RECON.FINDING_RESOLVED'],
  },
  {
    group: 'Allocation',
    events: ['ALLOCATION.GRANTED', 'ALLOCATION.REVOKED', 'ALLOCATION.TRANSFERRED'],
  },
  {
    group: 'Trip',
    events: ['TRIP.STARTED', 'TRIP.ENDED', 'TRIP.FORCE_CLOSED', 'TRIP.CANCELLED', 'TRIP.METRICS_COMPUTED'],
  },
  {
    group: 'Trip request',
    events: [
      'TRIP_REQUEST.CREATED',
      'TRIP_REQUEST.AUTO_APPROVED',
      'TRIP_REQUEST.APPROVED',
      'TRIP_REQUEST.REJECTED',
      'TRIP_REQUEST.CANCELLED',
      'TRIP_REQUEST.EXPIRED',
      'TRIP_REQUEST.CASCADE_APPROVED',
    ],
  },
  {
    group: 'Weekly plan',
    events: [
      'WEEKLY_PLAN.CREATED',
      'WEEKLY_PLAN.APPROVED',
      'WEEKLY_PLAN.REJECTED',
      'WEEKLY_PLAN.CANCELLED',
      'WEEKLY_PLAN.EXPIRED',
    ],
  },
  {
    group: 'Approval',
    events: ['APPROVAL.MAPPED_MANAGER_REPOINTED', 'APPROVAL.TAT_NUDGE'],
  },
  {
    group: 'Conduct / compliance',
    events: [
      'CONDUCT.DNC_OVERRIDE_USED',
      'CONDUCT.OUTSIDE_HOURS_OVERRIDE_USED',
      'CONDUCT.FREQUENCY_OVERRIDE_USED',
      'CONDUCT.OVERRIDE_USED',
    ],
  },
  {
    group: 'Customer / DPDP',
    events: [
      'CUSTOMER.DNC_FLAG_SET',
      'CUSTOMER.DNC_FLAG_REMOVED',
      'CUSTOMER.PII_ERASURE_REQUESTED',
      'CUSTOMER.PII_ERASED',
      'CUSTOMER.PII_ERASURE_CANCELLED',
    ],
  },
  {
    group: 'Ingestion',
    events: ['INGESTION.BATCH_RECEIVED', 'INGESTION.BATCH_PROCESSED', 'INGESTION.BATCH_FAILED'],
  },
  {
    group: 'Org',
    events: ['ORG_NODE.CREATED', 'ORG_NODE.UPDATED', 'ORG_NODE.ARCHIVED'],
  },
  {
    group: 'Tenant',
    events: ['TENANT.ONBOARDED', 'TENANT.SETTINGS_UPDATED', 'TENANT.FROZEN', 'TENANT.DEK_ROTATED'],
  },
  {
    group: 'User',
    events: [
      'USER.CREATED',
      'USER.ACTIVATED',
      'USER.SUSPENDED',
      'USER.OFFBOARDED',
      'USER.REACTIVATED',
      'USER.PASSWORD_RESET_BY_ADMIN',
      'USER.PASSWORD_CHANGED',
      'USER.TOTP_REQUIRED_ENABLED',
      'USER.TOTP_REQUIRED_DISABLED',
    ],
  },
  {
    group: 'Security / sign-in',
    events: [
      'SECURITY.LOGIN_SUCCEEDED',
      'SECURITY.LOGIN_FAILED',
      'SECURITY.LOGIN_POLICY_BLOCKED',
      'SECURITY.TOKEN_REFRESHED',
      'SECURITY.OTP_ISSUED',
      'SECURITY.OTP_ISSUE_SKIPPED',
      'SECURITY.TOTP_ENROLLED',
      'SECURITY.TOTP_REBOUND',
      'SECURITY.TOTP_LOGIN_CHALLENGE_COMPLETED',
      'SECURITY.TOTP_RESET_BY_ADMIN',
      'SECURITY.PII_TRAIL_VIEWED',
    ],
  },
  {
    group: 'Notification outbox (system)',
    events: [
      'SMS.TEMPLATED_SENT',
      'SMS.TEMPLATED_SEND_FAILED',
      'SMS.TEMPLATED_SKIPPED',
      'SMS.ENQUEUED',
      'SMS.SKIPPED',
      'SMS.SENT',
      'SMS.SEND_DEAD_LETTERED',
      'EMAIL.ENQUEUED',
      'EMAIL.SKIPPED',
      'EMAIL.SENT',
      'EMAIL.SEND_DEAD_LETTERED',
    ],
  },
]
